<template>
  <div
    v-if="visible"
    class="payment-countdown"
  >
    <div class="payment-countdown-inner">
      <div
        v-if="activityIcon"
        class="countdown-icon"
      >
        <img :src="activityIcon" />
      </div>
      <div
        class="countdown-label"
        @click="handleClick"
        v-html="label"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { durationFormatter } from '@/utils'
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

interface Props {
  expireTime?: string | number
  activityText?: string
  activityIcon?: string
  onEnd?: () => void
  onLink?: () => void
}

const props = defineProps<Props>()

const { t } = useI18n()
const visible = ref(false)
const label = ref('')
let countdownTimer: ReturnType<typeof setTimeout> | undefined

const setCountdown = (lastTime?: number) => {
  clearTimeout(countdownTimer)
  const activityText = props.activityText || ''
  const expireTime = new Date(Number(props.expireTime)).getTime()
  const now = Date.now()
  if (expireTime <= now) {
    visible.value = false
    if (lastTime) {
      setTimeout(() => {
        props.onEnd?.()
      }, 1000)
    }
    return
  }
  if (activityText.indexOf('%s') === -1) {
    label.value = activityText
  } else {
    let formatLabel = ''
    const { dd = 0, hh = 0, mm = 0, ss = 0 } = durationFormatter((expireTime - now) / 1000)
    dd && (formatLabel += t('how-many-days', [`<span class="time">${dd}</span>`]))
    if (dd || hh) {
      formatLabel += t('how-many-hours', [`<span class="time">${String(hh).padStart(2, '0')}</span>`])
    }
    if (dd || hh || mm) {
      formatLabel += t('how-many-minutes', [`<span class="time">${String(mm).padStart(2, '0')}</span>`])
    }
    formatLabel += t('how-many-seconds', [`<span class="time">${String(ss).padStart(2, '0')}</span>`])
    label.value = activityText.replace('%s', `<span class="format">${formatLabel}</span>`)
  }
  const n = Date.now()
  let delay = !lastTime ? 1000 : 1000 + (1000 - (n - lastTime))
  if (delay < 800 || delay > 1200) {
    delay = 1000 // 重新修正时间
  }
  countdownTimer = setTimeout(() => {
    setCountdown(n)
  }, delay)
}

const init = () => {
  const activityText = props.activityText || ''
  if (!props.expireTime && activityText && activityText.indexOf('%s') === -1) {
    // 文案中不含格式化转义符“%s”，或者没有有效的截止时间
    label.value = activityText
    visible.value = true
    return
  }
  if (props.expireTime && activityText) {
    visible.value = true
    setCountdown()
  }
}

const handleClick = async (e: MouseEvent) => {
  e.preventDefault()
  e.stopPropagation()
  const target = e.target as HTMLAnchorElement
  if (target.nodeName?.toLowerCase() === 'a') {
    // 是a标签
    props.onLink?.()
    const targetBlank = target.search?.indexOf('target=_blank') !== -1
    const hrefTarget = targetBlank ? '_blank' : '_self'
    window.open(target.href, hrefTarget)
  }
}

onMounted(() => {
  init()
})

onUnmounted(() => {
  clearTimeout(countdownTimer)
})
</script>

<style lang="less" scoped>
.payment-countdown {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 9px 24px;
  margin-bottom: 12px;

  &-inner {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
  }

  .countdown {
    &-icon {
      width: 24px;
      height: 24px;
      flex: 0 0 auto;

      > img {
        width: 100%;
        height: 100%;
      }
    }

    &-label {
      flex: 1;
      line-height: 22px;
      font-size: 14px;
      color: var(--color-primary-text);
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
    }
  }
}

@media screen and (max-width: 768px) {
  .payment-countdown {
    .countdown-label {
      font-size: 13px;
    }
  }
}
</style>
<style lang="less">
.countdown-label {
  .format {
    color: #666;
    line-height: 24px;
    font-size: 13px;
  }

  .time {
    display: inline-block;
    min-width: 22px;
    height: 24px;
    padding: 0 4px;
    font-size: 13px;
    color: var(--color-primary-text);
    text-align: center;
    line-height: 24px;
    margin-left: 6px;
    margin-right: 2px;
    border-radius: 4px;
    background: #d9dfe4;
  }

  .link {
    color: #306eff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:focus,
    &:visited {
      text-decoration: none;
    }
  }
}
</style>
